<script setup lang="ts">
import { onUnmounted, watch } from "vue";

import {
  featureHighlightLearnMoreClicked,
  featureHighlightTooltipShown,
} from "@/analytics/events/featureHighlight";
import { trackEvent } from "@/analytics/track";
import { tooltipDelayToAppear } from "@/components/ui/BaseTooltip/baseTooltip";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { FeatureHighlight } from "@/model/featureHighlight";

const props = defineProps<{ highlight: FeatureHighlight; title: string; visible: boolean }>();

// the highlight tooltip opens faster than usual, so a pointer only passing by would count as shown
let shownTimer: ReturnType<typeof setTimeout> | undefined;
watch(
  () => props.visible,
  (visible) => {
    clearTimeout(shownTimer);
    if (visible) {
      shownTimer = setTimeout(
        () => trackEvent(featureHighlightTooltipShown(props.highlight.id)),
        tooltipDelayToAppear,
      );
    }
  },
);
onUnmounted(() => clearTimeout(shownTimer));

function learnMore() {
  trackEvent(featureHighlightLearnMoreClicked(props.highlight.id));
  props.highlight.learnMore!();
}
</script>

<template>
  <!-- the tooltip is in <body>: without these a click on "Learn more" deselects the sticky -->
  <div class="feature-highlight-tooltip" data-ignore-click @pointerdown.stop>
    <span class="label">{{ $t("featureHighlight.label") }}</span>
    <span class="title">{{ title }}</span>
    <span class="description">{{ highlight.description }}</span>
    <button v-if="highlight.learnMore" type="button" class="learn-more" @click="learnMore">
      {{ $t("general.learnMore") }}
      <SvgIcon name="general/external-link" width="14" height="14" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";

.feature-highlight-tooltip {
  display: flex;
  flex-direction: column;
  max-width: 220px;

  .label {
    @include typography.extra-small("bold");

    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: colors.$alternative-yellow;
  }

  .title {
    @include typography.medium("semi-bold");

    margin-top: 6px;
  }

  .description {
    @include typography.small("regular");

    margin-top: 2px;
    line-height: 1.4;
    color: colors.$text-subtle-inverse;
  }

  .learn-more {
    @include typography.small("semi-bold");

    display: inline-flex;
    align-items: center;
    gap: 4px;
    align-self: flex-start;
    margin-top: 8px;
    color: colors.$alternative-yellow;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
